/**
 * Accessibility helpers (focus management).
 *
 * These are intentionally defined as top-level function declarations so they
 * are available globally (global.js is loaded as a classic deferred script) to
 * the other classic scripts (cart.js, cart-drawer.js) as well as the bundled
 * ES modules (via `window.trapFocus` / `window.removeTrapFocus`).
 */
function getFocusableElements(container) {
  if (!container) return [];
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  ).filter(
    (el) =>
      // Ignore elements that are not actually rendered / reachable.
      !el.closest('[inert]') &&
      !el.closest('[aria-hidden="true"]') &&
      (el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement),
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  if (!container) return;

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    // Attach the keydown handler as soon as focus enters the container, whatever
    // the exact target is. This keeps the trap working even when the set of
    // focusable elements changes (eg. cart items added/removed, async recos).
    if (!container.contains(event.target)) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () => {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = (event) => {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key

    // Recompute the focusable elements on every Tab so the boundaries stay
    // accurate when the container's content changes dynamically.
    const elements = getFocusableElements(container);
    if (elements.length === 0) {
      // Nothing to move to: keep focus on the container.
      event.preventDefault();
      return;
    }
    const first = elements[0];
    const last = elements[elements.length - 1];
    const active = document.activeElement;

    // On the last focusable element (or the container) and tab forward, focus
    // the first element.
    if (!event.shiftKey && (active === last || active === container)) {
      event.preventDefault();
      first.focus();
    }

    // On the first focusable element (or the container) and tab backward, focus
    // the last element.
    if (event.shiftKey && (active === first || active === container)) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (elementToFocus && typeof elementToFocus.focus === 'function') {
    elementToFocus.focus({preventScroll: true});

    if (
      elementToFocus.tagName === 'INPUT' &&
      ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
      elementToFocus.value
    ) {
      elementToFocus.setSelectionRange(0, elementToFocus.value.length);
    }
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus && typeof elementToFocus.focus === 'function')
    elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.max = Number(this.input.getAttribute('max'));
    this.min = Number(this.input.getAttribute('min'));
    this.plusBtn = this.querySelector('button[name="plus"]')
    this.minusBtn = this.querySelector('button[name="minus"]')
    this.changeEvent = new Event('change', { bubbles: true })
    if(this.getAttribute('data-origin') == 'product'){
      this.pricelist = document.querySelector('[data-price-list]') ?  JSON.parse(document.querySelector('[data-price-list]').textContent) : null;
      this.dynamicPrice = document.querySelectorAll('.dynamic-price')
      this.selectsQty = document.querySelectorAll('.quantity__select')
    }
    this.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this))
      this.updateBtnState()
    });

    document.addEventListener('quantity:changed', this.updateBtnState.bind(this) )

    this.input.addEventListener('input', (e) => {
      const value = Number(e.target.value);
      if (value > this.max) {
        this.input.value = this.max;
        this.input.dispatchEvent(this.changeEvent);
      }
      if (value < this.min) {
        this.input.value = this.min;
        this.input.dispatchEvent(this.changeEvent);
      }
      this.updateBtnState();
      this.syncSelectsWithInput();
      if (this.getAttribute('data-origin') === 'product' && this.pricelist) {
        this.dynamicPrice?.forEach(price => {
          price.innerHTML = this.pricelist[this.input.value];
        });
      }
    });
   
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    if (this.getAttribute('data-origin') === 'product') {
      this.dynamicPrice?.forEach(price => {
        price.innerHTML = this.pricelist[this.input.value];
      });
      this.syncSelectsWithInput();
    }
    this.updateBtnState();
  }

  syncSelectsWithInput() {
    const value = this.input.value;
    this.selectsQty?.forEach(select => Shopify.setSelectorByValue(select, value));
  }

  updateBtnState(){
    const isAtMin = this.input.value == this.min;
    const isAtMax = this.input.value == this.max;

    this.minusBtn.classList.toggle('limit-reached', isAtMin);
    this.plusBtn.classList.toggle('limit-reached', isAtMax);
  }
}

customElements.define('quantity-input', QuantityInput);
class QuantitySelect extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select');
    this.input = document.querySelector('#main-button .quantity__input-product');
    this.changeEvent = new Event('change', { bubbles: true })
    this.pricelist = JSON.parse(document.querySelector('[data-price-list]').textContent);
    this.dynamicPrice = document.querySelector('#main-button .dynamic-price')
    this.select.addEventListener('change', this.onChange.bind(this))
  }

  onChange(event) {
    event.preventDefault();
    const previousValue = this.select.value;

    if (previousValue !== this.select.value) this.select.dispatchEvent(this.changeEvent); 
    this.dynamicPrice.innerHTML = this.pricelist[this.select.value]
    this.input.value = this.select.value;
    const quantityChanged = new CustomEvent('quantity:changed')
    document.dispatchEvent(quantityChanged)
    
  }
}

customElements.define('quantity-select', QuantitySelect);


/*
 * Shopify Common JS
 *
 */
function debounce(fn, wait) {
	let t;
	return (...args) => {
		clearTimeout(t);
		t = setTimeout(() => fn.apply(this, args), wait);
	};
}

function fetchConfig(type = 'json') {
	return {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
	};
}

if ((typeof window.Shopify) == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function(fn, scope) {
  return function() {
    return fn.apply(scope, arguments);
  }
};

Shopify.setSelectorByValue = function(selector, value) {
  for (let i = 0, count = selector.options.length; i < count; i++) {
    let option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function(target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent('on'+eventName, callback);
};

Shopify.postLink = function(path, options) {
  options = options || {};
  let method = options['method'] || 'post';
  let params = options['parameters'] || {};

  let form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for(let key in params) {
    let hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function(country_domid, province_domid, options) {
  this.countryEl         = document.getElementById(country_domid);
  this.provinceEl        = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler,this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function() {
    let value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function() {
    let value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function(e) {
    let opt       = this.countryEl.options[this.countryEl.selectedIndex];
    let raw       = opt.getAttribute('data-provinces');
    let provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (let i = 0; i < provinces.length; i++) {
        opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function(selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function(selector, values) {
    for (let i = 0; i < values.length; i++) {
      let opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  }
};
class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this, false)
    );
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('u-overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
  }

  hide() {
    document.body.classList.remove('u-overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
  }
}

customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.updatePickupAvailability();
    this.removeErrorMessage();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();

      if(this.dataset.isGiftCard == 'true'){
        this.updateDynamicPrice();
      }
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;

    const mediaId = this.currentVariant.featured_media?.id || null;

    const mediaGallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
    if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function' && mediaId) {
      mediaGallery.setActiveMedia(`${this.dataset.section}-${mediaId}`, true);
    }

    // Custom event to let sliders react to variant media changes
    window.dispatchEvent(
      new CustomEvent('clrz:variant-media-change', {
        detail: {
          sectionId: this.dataset.section,
          mediaId,
          variantId: this.currentVariant.id,
        },
      })
    );

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    if (!modalContent) return;
    if (!mediaId) return;
    const newMediaModal = modalContent.querySelector(`[data-media-id="${mediaId}"]`);
    if (!newMediaModal) return;
    modalContent.prepend(newMediaModal);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    // Support multiple product forms (main and sticky) by matching id prefix
    const productForms = document.querySelectorAll(
      `form[id^="product-form-${this.dataset.section}"], form[id^="product-form-installment-${this.dataset.section}"]`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (!input) return;
      if (input.hasAttribute('disabled')) input.removeAttribute('disabled');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(`price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        if (source && destination) destination.innerHTML = source.innerHTML;

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove('visibility-hidden');
        this.toggleAddButton(window.variantStrings.soldOut, !this.currentVariant.available);
      });
  }

  toggleAddButton(text, disable = true, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }

  updateDynamicPrice() {
    const dynamicPrice = document.querySelectorAll('[data-dynamic-price]');
    if (!dynamicPrice) return;
    dynamicPrice.forEach(price => {
      // Format price with currency
      const formattedPrice = this.formatPrice(this.currentVariant.price);
      price.innerHTML = formattedPrice;
    });
  }

  formatPrice(price) {
    // Convert price from cents to dollars/euros and format with currency
    const priceInCurrency = (price / 100).toFixed(2);
    const currencySymbol = window.global?.currency || '€';
    return `${priceInCurrency} ${currencySymbol}`;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('variant-radios', VariantRadios);


class LanguageForm extends HTMLElement {
  constructor() {
    super()
    this.elements = {
      input: this.querySelector('input[name="language_code"], input[name="country_code"]'),
      button: this.querySelector("button"),
      panel: this.querySelector("ul"),
    }
    this.elements.button.addEventListener("click", this.openSelector.bind(this))
    this.elements.button.addEventListener("focusout", this.closeSelector.bind(this))
    this.addEventListener("keyup", this.onContainerKeyUp.bind(this))

    this.querySelectorAll("a").forEach(item => item.addEventListener("click", this.onItemClick.bind(this)))
  }

  hidePanel() {
    this.elements.button.setAttribute("aria-expanded", "false")
    this.elements.panel.setAttribute("hidden", true)
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return

    this.hidePanel()
    this.elements.button.focus()
  }

  onItemClick(event) {
    event.preventDefault()
    const form = this.querySelector("form")
    this.elements.input.value = event.currentTarget.dataset.value
    if (form) form.submit()
  }

  openSelector() {
    this.elements.button.focus()
    this.elements.panel.toggleAttribute("hidden")
    this.elements.button.setAttribute(
      "aria-expanded",
      (this.elements.button.getAttribute("aria-expanded") === "false").toString()
    )
  }

  closeSelector(event) {
    const shouldClose = event.relatedTarget && event.relatedTarget.nodeName === "BUTTON"
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel()
    }
  }
}

customElements.define("language-form", LanguageForm)